import {
  EmailBlockType,
  Section,
  SharedProps,
} from "@redotech/redo-model/email-builder";
import {
  buttonDefault,
  columnDefault,
  discountDefault,
  footerDefault,
  headerDefaultLogo,
  imageDefault,
  interactiveCartDefault,
  lineDefault,
  menuDefault,
  reviewRequestDefault,
  socialsDefault,
  spacerDefault,
  textDefault,
  trackableSummaryDefault,
  trackingInfoDefault,
} from "@redotech/redo-model/email-template-defaults";
import {
  WidgetEditorProps,
  withEditor,
} from "../../form-builder/widget-editors/widget-editor-decorator";
import { StaticRecommendedProduct } from "../../product-recommendations";
import { EmailButton } from "../blocks/button";
import { EmailColumn } from "../blocks/column";
import { EmailDiscount } from "../blocks/discount";
import { EmailFooter } from "../blocks/footer";
import { EmailHeader } from "../blocks/header";
import { EmailImage } from "../blocks/image";
import { EmailInteractiveCart } from "../blocks/interactive-cart";
import { EmailLine } from "../blocks/line";
import { EmailMenu } from "../blocks/menu";
import { EmailOrderSummary } from "../blocks/order-summary";
import { EmailReviewRequest } from "../blocks/review-request";
import { EmailSocials } from "../blocks/socials";
import { EmailSpacer } from "../blocks/spacer";
import { EmailText } from "../blocks/text";
import { EmailTrackingInfo } from "../blocks/tracking-info";
import { ColumnEditor } from "./column-editor";
import {
  IsolatedWidgetElementEditor,
  RichTextEditor,
} from "./email-builder-editor-wrappers";
import { MenuEditor } from "./menu-editor";
import { SocialsEditor } from "./socials-editor";

export interface EmailBlockProps<T extends Section> extends SharedProps {
  element: T;
  extraProps: Record<string, any>; // escape hatch, be careful with it
}

export interface EmailBlockEditorProps<T extends Section>
  extends WidgetEditorProps<EmailBlockProps<T>, T> {
  widget: EmailBlockProps<T>;
}

export const editorPreviews: Record<
  EmailBlockType,
  React.FC<EmailBlockEditorProps<any>>
> = {
  [EmailBlockType.TEXT]: withEditor(
    RichTextEditor,
    (props: EmailBlockProps<Section.Text>) => (
      <EmailText {...props.element} {...props} />
    ),
  ),
  [EmailBlockType.BUTTON]: withEditor(
    IsolatedWidgetElementEditor,
    (props: EmailBlockProps<Section.Button>) => (
      <EmailButton {...props.element} {...props} />
    ),
  ),
  [EmailBlockType.IMAGE]: withEditor(
    IsolatedWidgetElementEditor,
    (props: EmailBlockProps<Section.Image>) => (
      <EmailImage {...props.element} {...props} />
    ),
  ),
  [EmailBlockType.LINE]: withEditor(
    IsolatedWidgetElementEditor,
    (props: EmailBlockProps<Section.Line>) => (
      <EmailLine {...props.element} {...props} />
    ),
  ),
  [EmailBlockType.SPACER]: withEditor(
    IsolatedWidgetElementEditor,
    (props: EmailBlockProps<Section.Spacer>) => (
      <EmailSpacer {...props.element} {...props} />
    ),
  ),
  [EmailBlockType.HEADER]: withEditor(
    IsolatedWidgetElementEditor,
    (props: EmailBlockProps<Section.Header>) => (
      <EmailHeader {...props.element} {...props} />
    ),
  ),
  [EmailBlockType.FOOTER]: withEditor(
    IsolatedWidgetElementEditor,
    (props: EmailBlockProps<Section.Footer>) => (
      <EmailFooter {...props.element} {...props} />
    ),
  ),
  [EmailBlockType.TRACKING_INFO]: withEditor(
    IsolatedWidgetElementEditor,
    (props: EmailBlockProps<Section.TrackingInfo>) => (
      <EmailTrackingInfo {...props.element} {...props} />
    ),
  ),
  [EmailBlockType.INTERACTIVE_CART]: withEditor(
    IsolatedWidgetElementEditor,
    (props: EmailBlockProps<Section.InteractiveCart>) => {
      const staticRecommendedProducts: StaticRecommendedProduct[] | undefined =
        props.extraProps.staticRecommendedProducts;

      return (
        <EmailInteractiveCart
          {...props.element}
          {...props}
          staticRecommendedProducts={
            staticRecommendedProducts ?? dummyStaticRecommendedProducts
          }
        />
      );
    },
  ),
  [EmailBlockType.TRACKABLE_SUMMARY]: withEditor(
    IsolatedWidgetElementEditor,
    (props: EmailBlockProps<Section.TrackableSummary>) => (
      <EmailOrderSummary {...props.element} {...props} />
    ),
  ),
  [EmailBlockType.REVIEW_REQUEST]: withEditor(
    IsolatedWidgetElementEditor,
    (props: EmailBlockProps<Section.ReviewRequest>) => (
      <EmailReviewRequest {...props.element} {...props} />
    ),
  ),
  [EmailBlockType.COLUMN]: withEditor(
    ColumnEditor,
    (props: EmailBlockProps<Section.Column>) => (
      <EmailColumn {...props.element} {...props} />
    ),
  ),
  [EmailBlockType.MENU]: withEditor(
    MenuEditor,
    (props: EmailBlockProps<Section.Menu>) => (
      <EmailMenu {...props.element} {...props} />
    ),
  ),
  [EmailBlockType.SOCIALS]: withEditor(
    SocialsEditor,
    (props: EmailBlockProps<Section.Socials>) => (
      <EmailSocials {...props.element} {...props} />
    ),
  ),
  [EmailBlockType.DISCOUNT]: withEditor(
    IsolatedWidgetElementEditor,
    (props: EmailBlockProps<Section.Discount>) => (
      <EmailDiscount {...props.element} {...props} discountCode={undefined} />
    ),
  ),
};

export const defaultBlocks: Record<EmailBlockType, Section> = {
  [EmailBlockType.HEADER]: headerDefaultLogo,
  [EmailBlockType.FOOTER]: footerDefault,
  [EmailBlockType.TEXT]: textDefault,
  [EmailBlockType.BUTTON]: buttonDefault,
  [EmailBlockType.IMAGE]: imageDefault,
  [EmailBlockType.SPACER]: spacerDefault,
  [EmailBlockType.LINE]: lineDefault,
  [EmailBlockType.TRACKING_INFO]: trackingInfoDefault,
  [EmailBlockType.TRACKABLE_SUMMARY]: trackableSummaryDefault,
  [EmailBlockType.REVIEW_REQUEST]: reviewRequestDefault,
  [EmailBlockType.INTERACTIVE_CART]: interactiveCartDefault,
  [EmailBlockType.COLUMN]: columnDefault,
  [EmailBlockType.MENU]: menuDefault,
  [EmailBlockType.SOCIALS]: socialsDefault,
  [EmailBlockType.DISCOUNT]: discountDefault,
};

const dummyStaticRecommendedProducts = Array.from(
  { length: 10 },
  (_, i) => i + 1,
).map((i) => ({
  id: `product-${i}`,
  title: `Product ${i}`,
  variantTitle: `Variant ${i}`,
  price: i * 10,
  image: { src: `https://via.placeholder.com/150?text=Product+${i}` },
  pdpUrl: "",
  quantity: 1,
  alreadyInCart: false,
}));
